export interface Cookie {
  readonly name: string;
  readonly value: string;
}

export interface CookieJarReadResult {
  readonly cookieHeader: string | null;
  readonly ua: string | null;
}

interface Entry {
  readonly cookies: readonly Cookie[];
  readonly ua: string;
}

const CF_NAME_PATTERN = /^(cf_clearance|__cf_bm|cf_chl)/;

export function isCfCookie(name: string): boolean {
  return CF_NAME_PATTERN.test(name);
}

export class CookieJar {
  readonly #byHost = new Map<string, Entry>();

  put(host: string, cookies: readonly Cookie[], ua: string): void {
    // The UA pin is needed for HTTP-branch session continuity even when CF
    // hasn't issued cookies yet — dropping the entry on `filtered.length === 0`
    // would let the rotating UA client churn UAs until the first challenge.
    const filtered = cookies.filter((c) => isCfCookie(c.name));
    const key = host.toLowerCase();
    const existing = this.#byHost.get(key);
    const nextCookies = filtered.length > 0 ? filtered : (existing?.cookies ?? []);
    this.#byHost.set(key, { cookies: nextCookies, ua });
  }

  read(host: string): CookieJarReadResult {
    const entry = this.#byHost.get(host.toLowerCase());
    if (!entry) return { cookieHeader: null, ua: null };
    const cookieHeader =
      entry.cookies.length > 0
        ? entry.cookies.map((c) => `${c.name}=${c.value}`).join("; ")
        : null;
    return { cookieHeader, ua: entry.ua };
  }
}
